/******
 * View for the ctd lab
 * There are two separate view involved within this,
 *    1. Table View: for users to play around and try creating manual test cases.
 *    2. Tool View: provides the users with the CTD tool that generates test cases.
 ***********/
import Styles from './NodeCTD.module.scss'
import { useRef, useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// module imports
import axios from 'axios';
import _ from 'lodash'
import { ToastContainer, toast } from 'react-toastify';
import { Avatar, Typography, Dialog, Fab } from '@mui/material';
import { pink } from '@mui/material/colors';
import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';

// component imports
import { Feedback, useAPI, AuthContext, ViewContext, useCan } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { CombTestPane } from 'components/global/NodeLayout/CombinatorialTestPane/CombinatorialTestPane';
import InfoDrawer from 'components/global/NodeLayout/InfoDrawer';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { NodeTimeline } from 'components/global/NodeLayout/NodeTimeline';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { BackConfirmationCard } from 'components/global/BackConfirmationCard/BackConfirmationCard';
import { HelperChat } from 'components/global/HelperChat/HelperChat';

// icons
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VisibilityIcon from '@mui/icons-material/Visibility';

// utils
import { decoder } from 'utils/helpers/decoder';
import RevealAnswerKey from 'components/RevealAnswerKey/RevealAnswerKey';
import { InstructionPane } from 'components/global/InstructionPane/InstructionPane';
import { copyEnabledChecker } from 'utils/helpers/utility';

// eslint-disable-next-line no-unused-vars
const finalAudio = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}/media/git/voice/finish_node.mp3`;

export function NodeCTD(props) {

    const { node_id } = useParams();
    const { lab_id } = useParams();
    const { resource } = useParams();
    const navigate = useNavigate();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [nodeInfo, setNodeInfo] = useState();
    const [isMuted, setIsMuted] = useState(true);
    const [nodeFinished, setNodeFinished] = useState(false);
    const [viewReference, setViewReference] = useState(false)
    const [steps, setSteps] = useState()
    const [toolParameters, setToolParameters] = useState({
        parameters: [],
        constraints: [],
        coverage: 2,
        requiredTests: [],
        testTemplate: '',

    })
    const [validParameters, setValidParameters] = useState(true);
    const { data, loading } = useAPI(`/api/labs/${lab_id}/nodes/${node_id}/${resource}`);
    const currentRef = useRef(null);
    const [tests, setTests] = useState({
        headers: ['', ''],
        cases: [
            ['', '']
        ]
    })
    const [startLab, setStartLab] = useState(true);
    const [maxUnlocked, setMaxUnlocked] = useState(0);
    const [copied, setCopied] = useState(false);

    const roundedPercentage = nodeFinished ? 100 : Math.ceil(currentStep / nodeInfo?.numOfSteps * 100);

    const context = useContext(AuthContext);
    const [showReveal, setShowReveal] = useState(false);
    const audioRef = useRef(null);
    const finalAudioRef = useRef(null);
    const viewContext = useContext(ViewContext)
    const isCopyEnabled = copyEnabledChecker(context?.user)


    useEffect(() => {
        if (data) {
            const jsonData = decoder(data);
            jsonData.numOfSteps = jsonData?.steps?.steps?.length;
            setNodeInfo(jsonData);
            setSteps(jsonData?.steps?.steps);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    //in table view, if the steps have any header, this useEffect ensures those headers are displayed
    useEffect(() => {
        if (steps) {
            if (steps[currentStep]?.headers) {
                if (tests.headers.length < steps[currentStep].headers.length) {
                    const remaining = steps[currentStep].headers.length - tests.headers.length
                    for (let i = 0; i < remaining; i++) {
                        addNewParameter();
                    }
                }
                setTests(prevState => ({
                    cases: [
                        Array(steps[currentStep].headers.length).fill("")
                    ],
                    headers: steps[currentStep].headers
                }))

            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, loading, steps])


    useEffect(() => {
        document.title = nodeInfo?.name
    }, [nodeInfo])
    function displayError(errMsg) {
        toast.dark(errMsg, {
            type: 'error',
            position: 'top-right',
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });

    }

    useEffect(() => {
        if (!steps) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audioLink = `${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.voice}`;
        if (audioLink && !isMuted) {
            const newAudio = new Audio(audioLink);
            audioRef.current = newAudio;
            newAudio.play();
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isMuted]);

    useEffect(() => {
        if (!nodeFinished) return;

        if (audioRef.current) {
            audioRef.current.pause();
        }

        const audio = new Audio(finalAudio);
        finalAudioRef.current = audio;
        audio.play();

        viewContext.notification.show(
            props.t('Labs.Nodes.NodeCTD.finish'),
            'info',
            false
        );

        return () => {
            if (finalAudioRef.current) {
                finalAudioRef.current.pause();
            }
        };

    }, [nodeFinished])

    useEffect(() => {
        setMaxUnlocked(prev => Math.max(prev, currentStep));
    }, [currentStep]);

    const handleVoiceClick = () => {
        setIsMuted(!isMuted);
    };

    function handleHeaderChange(value, index) {
        //function used to add value in the table headers
        const newHeaders = [...tests.headers];
        newHeaders[index] = value;
        setTests(prevState => (
            {
                ...prevState,
                headers: newHeaders
            }
        ))
    }

    function handleCasesChange(value, combIndex, caseIndex) {
        //function to enable editing the table view and adding new values
        const newCases = [...tests.cases];
        newCases[combIndex][caseIndex] = value
        setTests(prevState => (
            {
                ...prevState,
                cases: newCases
            }
        ))
    }

    function deleteParameter(index) {
        //delete a column/parameter from table view
        const updated_headers = tests.headers.filter((_, i) => i !== index)
        const updated_cases = tests.cases.map((testCase) => {

            const filtered_test = testCase.filter((_, i) => i !== index);
            return filtered_test
        })
        setTests({
            headers: updated_headers,
            cases: updated_cases
        })
    }

    function deleteCase(index) {
        //function that deletes a row/case from the table
        const cases = tests.cases.filter((_, i) => i !== index);
        setTests(prevState => (
            {
                ...prevState,
                cases: cases
            }
        ))
    }

    function addNewRow() {
        //add new rows in the table view
        let newComb = [];
        tests?.headers?.forEach(() => {
            newComb.push('')
        })

        setTests(prevState => (
            {
                ...prevState,
                cases: [...prevState.cases, newComb]
            }
        ))
    }

    function addNewParameter() {
        //function to add new column/ parameter in the table view
        const current_cases = [...tests.cases];
        const new_cases = current_cases.map((cases) => {
            cases.push('');
            return cases
        })
        setTests(prevState => (
            {
                ...prevState,
                headers: [...prevState.headers, ''],
                cases: new_cases
            }
        ))
    }


    function compare_headers(expected, user_input) {
        //function to compare the header/parameter title input by users in the table view
        if (!expected?.exp_headers) {
            return true
        }
        const exp_comb = expected.exp_headers
        const comb_length = exp_comb?.length;

        if (user_input.length !== comb_length) {

            return false;
        }

        const isValidHeaders = user_input.every((val, index) => val === exp_comb[index])
        if (!isValidHeaders) {
            return false;
        }

        return true;
    }


    function arraysMatch(arr1, arr2) {
        return JSON.stringify(arr2).includes(JSON.stringify(arr1));
    }

    function compare_cases(expected, user_input) {
        //function to compare the cases/parameter values input by users in the table view
        if (!expected.validations_required) {
            return true;
        }
        if (!expected?.exp_cases) {
            return true
        }
        const exp_comb = expected.exp_cases
        const comb_length = exp_comb.length;

        if (user_input.length !== comb_length) {
            return false;
        }

        for (let i = 0; i < exp_comb.length; i++) {
            if (!arraysMatch(exp_comb[i], user_input)) {
                return false;
            }
        }

        return true;
    }


    function compare_parameters(expected, user_input) {
        //function to compare what the users input vs the expected input in the tool
        const arraysAreEqual = (arr1, arr2) => {
            if (arr1.length !== arr2.length) return false;
            return arr1.every(value => arr2.includes(value));
        };

        //parameters
        if (user_input.parameters.length !== expected.parameters.length) {
            return 'invalidParams';
        }
        for (let expItem of expected.parameters) {
            let userItem = user_input.parameters.find(u => u.key === expItem.key);
            if (!userItem || !arraysAreEqual(expItem.values, userItem.values)) {
                return 'invalidParams'
            }
        }

        //constraints
        if (user_input.constraints.length !== expected.constraints.length) {
            return 'invalidConstraints'
        }

        const isValidConstrains = expected.constraints.every(expItem => {
            return user_input.constraints.some(userItem => {
                // return JSON.stringify(expItem) === JSON.stringify(userItem)
                return _.isEqual(expItem, userItem)
            })
        });


        if (!isValidConstrains) {
            return 'invalidConstraints'
        }

        //coverage
        if (parseInt(user_input.coverage) !== expected.coverage) {
            return 'invalidCoverage'
        }

        //required Tests
        if (user_input.requiredTests.length !== expected.requiredTests.length) {
            return 'invalidReqTests';
        }
        const isValidReqTests = expected.requiredTests.every(expItem => {
            return user_input.requiredTests.some(userItem => {
                return JSON.stringify(expItem) === JSON.stringify(userItem)
            })
        })

        if (!isValidReqTests) {
            return 'invalidReqTests';
        }

        //Test Scripts
        if (expected.testTemplate !== user_input.testTemplate) {
            return 'invalidTestScripts'
        }

        return 'valid'
    }

    function moveToNext() {

        if (currentStep === steps.length - 1) {
            setNodeFinished(true)
            return
        }
        setCurrentStep(currentStep + 1)
        if (nodeInfo?.resource?.config?.ctdTool) {
            setValidParameters(true)
        }
    }


    function compareInput(exp_comb, user_input) {
        //function that validates input from the table view
        if (
            !compare_headers(exp_comb, user_input?.headers) ||
            !compare_cases(exp_comb, user_input?.cases)
        ) {
            displayError(props.t('Labs.Nodes.NodeCTD.error.checkCombinations'));
            return;
        }

        if (steps[currentStep].clear) {
            setTests({
                headers: ['', ''],
                cases: [
                    ['', '']
                ]
            })
        }
        moveToNext();
    }

    function errorGenerator(validity) {
        let errMessage = ''
        switch (validity) {
            case 'invalidParams':
                errMessage = props.t('Labs.Nodes.NodeCTD.error.invalidParams')
                break;
            case 'invalidConstraints':
                errMessage = props.t('Labs.Nodes.NodeCTD.error.invalidConstraints')
                break;
            case 'invalidReqTests':
                errMessage = props.t('Labs.Nodes.NodeCTD.error.invalidReqTests')
                break;
            case 'invalidTestScripts':
                errMessage = props.t('Labs.Nodes.NodeCTD.error.invalidTestScripts')
                break;
            case 'invalidCoverage':
                errMessage = props.t('Labs.Nodes.NodeCTD.error.invalidCoverage')
                break;
            default:
                errMessage = props.t('Labs.Nodes.NodeCTD.error.invalidInputs')
        }
        return errMessage;
    }

    function compare_tool_input(exp_comb) {
        //function that validates input from the tool view
        const validity = compare_parameters(exp_comb, toolParameters)
        if (validity !== 'valid') {
            displayError(errorGenerator(validity) + props.t('Labs.Nodes.NodeCTD.error.regenerateTests'))
            setValidParameters(true)
            return
        }
        moveToNext()
    }

    const handleFinish = () => {
        const options = {
            method: "POST",
            url: `/api/user/labs/${lab_id}/nodes/${node_id}/end`,
            headers: {
                "accept": "application/json",
            },
            data: {
                ...(nodeInfo.is_assessment === 1 && { completionPercent: roundedPercentage }),
            },

        };
        axios
            .request(options)
            .then(async function (response) {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Adding a wait for api post to happen
                if (nodeInfo.is_assessment) {
                    navigate(`/labs/${lab_id}/nodes`)
                } else {
                    navigate(`/labs/${lab_id}/nodes/${node_id}/quiz/attempt`);
                }
            })
            .catch((err) => {
                navigate('/oops');
            });
    };

    const startLabClick = async () => {

        if (props?.live_assessment) {
            setStartLab(false);
        } else {
            const options = {
                method: 'POST',
                url: `/api/user/labs/${lab_id}/nodes/${node_id}/start`,
                headers: {
                    accept: 'application/json',
                },
            };
            await axios
                .request(options)
                .then((response) => {
                    new Promise((resolve) => setTimeout(resolve, 100));
                    setStartLab(false);
                    setIsMuted(!isMuted)
                })
                .catch((err) => {
                    if (err.response.status === 403) {
                        navigate('/nopermission');
                    } else {
                        navigate('/oops');
                    }
                });
        }
    };

    const handleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const enableVerify = () => {
        setValidParameters(false)
    }

    const isSupervisor = useCan('node.progress.flag');
    const isAssessmentNode = nodeInfo?.is_assessment;;
    const canReveal = !!(isSupervisor && isAssessmentNode);

    const handleCopy = async (e) => {
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 2000);
        toast.dark(props.t('Labs.Nodes.NodeAutomation.toast.warn'), {
            type: 'warning',
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    };

    const handleCopyDisabled = (e) => {
        e.preventDefault();
    }

    return (
        <>
            <ToastContainer
                id="warning-toast"
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ fontSize: "16px" }}
            />
            {loading ? (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            ) : steps ? (
                <>
                    <div className={Styles.Ancestor_Container}>
                        <ConfirmationCard
                            title={
                                nodeInfo.is_assessment ?
                                    props.t("Labs.Nodes.NodeCTD.confirm.start.assessment.title")
                                    : props.t("Labs.Nodes.NodeCTD.confirm.start.default.title")
                            }
                            body={
                                nodeInfo.is_assessment ?
                                    props.t("Labs.Nodes.NodeCTD.confirm.start.assessment.body")
                                    : props.t("Labs.Nodes.NodeCTD.confirm.start.default.body")
                            }
                            showCard={startLab}
                            setShowCard={setStartLab}
                            handleCancel={() => navigate(-1)}
                            handleConfirm={startLabClick}
                        />
                        {!startLab && (
                            <>
                                <BackConfirmationCard
                                    isSaveAvailable={false}
                                />
                                <div id="titleContainer" className={Styles.titleContainer}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        // border: '1px solid green'
                                    }}
                                    >
                                        <Avatar
                                            sx={{
                                                bgcolor: pink[500],
                                                alignSelf: 'center',
                                                mr: 1,
                                                width: 40,
                                                height: 40,
                                            }}
                                        >
                                            <SmartToyIcon sx={{ width: 38, height: 38 }} />
                                        </Avatar>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                textTransform: 'uppercase',
                                                color: '#76EAD7',
                                            }}
                                        >
                                            {nodeInfo?.name}
                                        </Typography>
                                    </div>
                                    <span>
                                        <AivagamButton
                                            type='action'
                                            buttonText={props.t('Labs.Nodes.NodeCTD.button.finish')}
                                            onClick={handleFinish}
                                            classStyles={{ borderRadius: "0.4em", marginRight: "0.5em" }}
                                            disabled={
                                                props.live_assessment ? false :
                                                    !nodeInfo.is_assessment
                                                        ? !nodeFinished
                                                        : roundedPercentage < 70
                                            }
                                            tooltipText={
                                                nodeInfo.is_assessment
                                                    ? ((roundedPercentage > 70) || nodeFinished ?
                                                        props.t('Labs.Nodes.NodeCTD.tooltip.finish.done')
                                                        : props.t('Labs.Nodes.NodeCTD.tooltip.finish.assessment')
                                                    )
                                                    : (nodeFinished ?
                                                        props.t('Labs.Nodes.NodeCTD.tooltip.finish.done')
                                                        : props.t('Labs.Nodes.NodeCTD.tooltip.finish.info')
                                                    )
                                            }
                                        />

                                        {canReveal && (
                                            <LightTooltip
                                                title={props.t('Labs.Nodes.NodeCTD.tooltip.revealAnswer')}
                                                arrow
                                                placement="left"
                                                classes={{
                                                    tooltip: {
                                                        backgroundColor: '#fff',
                                                        color: 'rgba(0, 0, 0, 0.87)',
                                                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                                        fontSize: 11,
                                                    },
                                                }}
                                            >
                                                <Fab
                                                    color="primary"
                                                    size="medium"
                                                    onClick={() => setShowReveal(true)}
                                                    style={{ marginRight: 8 }}
                                                >
                                                    <VisibilityIcon />
                                                </Fab>
                                            </LightTooltip>
                                        )}

                                        <LightTooltip
                                            title={props.t('Labs.Nodes.NodeCTD.tooltip.reference')}
                                            arrow
                                            placement="left"
                                            classes={{
                                                tooltip: {
                                                    backgroundColor: '#fff',
                                                    color: 'rgba(0, 0, 0, 0.87)',
                                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                                    fontSize: 11,
                                                },
                                            }}
                                        >
                                            <Fab color="primary" size="medium" onClick={handleDrawer}>
                                                <LightbulbIcon />
                                            </Fab>
                                        </LightTooltip>
                                    </span>
                                </div>
                                <div className={Styles.bodyContainer}>
                                    <div className={Styles.toolContainer}>
                                        <div className={Styles.validations_buttons}>
                                            <AivagamButton
                                                buttonText={props.t('Labs.Nodes.NodeCTD.button.verify')}
                                                type='action'
                                                id="verifyButton"
                                                classStyles={{
                                                    padding: '0.4em',
                                                    borderRadius: '8px',
                                                    width: '15%',
                                                    marginLeft: 'auto',
                                                    fontWeight: "700"
                                                }}
                                                onClick={() => { nodeInfo?.resource?.config?.ctdTool ? compare_tool_input(steps[currentStep]) : compareInput(steps[currentStep], tests) }}
                                                disabled={nodeInfo?.resource?.config?.ctdTool ? validParameters : false}
                                            />
                                        </div>

                                        {/* CTD Tool */}
                                        {nodeInfo?.resource?.config?.ctdTool && (
                                            <div className={Styles.ctd_tool_view}>
                                                <CombTestPane
                                                    isLab={true}
                                                    setLabParameters={setToolParameters}
                                                    resource={resource}
                                                    // generateDisable={validParameters}
                                                    nextStep={enableVerify}
                                                />
                                            </div>
                                        )}

                                        {/* Permutations & Combinations Tool */}
                                        {!nodeInfo?.resource?.config?.ctdTool && (
                                            <div id='permCombTool' className={Styles.perm_comb_view}>
                                                <table id='permcombTable' className={Styles.comb_table}>
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            {tests?.headers?.map((head, index) => {
                                                                return (
                                                                    <th key={`${index}_header`}>
                                                                        <div className={Styles.parameter_title_container}>
                                                                            <FeatherIcon icon='x' role='button' size='1.5em' color='#d3d3d3' onClick={() => { deleteParameter(index) }} />
                                                                            <textarea
                                                                                value={head}
                                                                                onChange={(e) => { handleHeaderChange(e.target.value, index) }}
                                                                                // onBlur={(e) => { e.target.setSelectionRange(0, 0); }}
                                                                                className={Styles.input_area}
                                                                                placeholder={props.t('Labs.Nodes.NodeCTD.placeholder.parameter', { index: index + 1 })}
                                                                            />
                                                                        </div>
                                                                    </th>
                                                                )
                                                            })}
                                                            <th style={{ maxWidth: '3em' }}>
                                                                <div className={Styles.row_col_add_container} >
                                                                    <FeatherIcon icon='plus' size='1.5em' color='#d3d3d3' role='button' onClick={addNewParameter} />
                                                                </div>
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {
                                                            tests?.cases.map((comb, combIndex) => (
                                                                <tr key={`${combIndex}_combination`}>
                                                                    <td>
                                                                        <div className={Styles.cases_row_container}>
                                                                            <FeatherIcon icon='x' size='1.5em' color='#d3d3d3' role='button' onClick={() => { deleteCase(combIndex) }} />
                                                                            <p style={{ width: 'fit-content' }}>{props.t('Labs.Nodes.NodeCTD.placeholder.row', { index: combIndex + 1 })} </p>
                                                                        </div>
                                                                    </td>
                                                                    {
                                                                        comb?.map((testCase, caseIndex) => (
                                                                            <td key={`${caseIndex}_case`}>
                                                                                <textarea
                                                                                    value={testCase}
                                                                                    onChange={(e) => { handleCasesChange(e.target.value, combIndex, caseIndex) }}
                                                                                    className={Styles.input_area}
                                                                                    placeholder={props.t('Labs.Nodes.NodeCTD.placeholder.enterValue')}
                                                                                />
                                                                            </td>
                                                                        ))
                                                                    }
                                                                </tr>
                                                            ))
                                                        }
                                                        <tr></tr>
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td style={{ padding: '0', height: '3em' }}>
                                                                <div className={Styles.row_col_add_container} >
                                                                    <FeatherIcon icon='plus' size='1.5em' color='#d3d3d3' role='button' onClick={addNewRow} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                    <div data-testid="nodeCtd-bodyContainer-instructionContainer" id="instructionContainer" className={Styles.instructionContainer}>
                                        <InstructionPane
                                            props={props}
                                            steps={steps}
                                            currentStep={currentStep}
                                            isMuted={isMuted}
                                            setIsMuted={setIsMuted}
                                            onHandleVoiceClick={handleVoiceClick}
                                            nodeInfo={nodeInfo}
                                            setViewReference={setViewReference}
                                            onHandleCopy={handleCopy}
                                            onHandleCopyDisabled={handleCopyDisabled}
                                            copied={copied}
                                            showLineNumbers={false}
                                            nodeCTD={true}
                                            nodeFinished={nodeFinished}
                                            theme="light"
                                            isCopyEnabled={isCopyEnabled}
                                            displayCopyButton={false}
                                        />
                                    </div>
                                    <span data-testid="nodeCtd-bodyContainer-codeEditorTimeLine" id="codeEditorTimeLine" className={Styles.codeEditorTimeLine}>
                                        <NodeTimeline
                                            nodeFinished={nodeFinished}
                                            currentStep={currentStep}
                                            steps={steps}
                                            currentRef={currentRef}
                                            hideToolTip={true}
                                            collapsable={true}
                                            setCurrentStep={setCurrentStep}
                                            maxUnlocked={maxUnlocked}
                                        />
                                    </span>
                                </div>
                                {(!startLab && !nodeFinished && nodeInfo.is_assessment !== 1 && nodeInfo?.resource?.config?.chatbotTopic) && (
                                    <HelperChat
                                        active={props.aiChat}
                                        topic={nodeInfo?.resource?.config?.chatbotTopic}
                                    />
                                )}
                            </>
                        )}
                        <Dialog
                            open={viewReference}
                            onClose={() => { setViewReference(!viewReference) }}
                        >
                            <div style={{ padding: '0.5em' }}>
                                {steps[currentStep]?.reference?.type === 'video' &&
                                    <video src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.reference?.content}`}
                                        alt='reference' autoplay controls />
                                }
                                {steps[currentStep]?.reference?.type === 'image' &&
                                    <img src={`${process.env.REACT_APP_GLOBAL_MEDIA_URL}${steps[currentStep]?.reference?.content}`} alt='reference' />
                                }
                            </div>
                        </Dialog>

                        <InfoDrawer
                            handleDrawer={handleDrawer}
                            drawerOpen={drawerOpen}
                            titleText={`${nodeInfo?.labName} reference`}
                            displayData={nodeInfo?.content?.reference_data}
                        />
                    </div>
                    {nodeFinished && <Feedback isInNode={true} nodeName={nodeInfo.name} labName={nodeInfo.labName} />}

                    <RevealAnswerKey
                        showAnswerKeyDialog={showReveal}
                        setShowAnswerKeyDialog={setShowReveal}
                        steps={steps}
                        currentStep={currentStep}
                        language={"plaintext"}
                        nodeId={node_id}
                        nodeName={nodeInfo?.name}
                        labTitle={nodeInfo?.labName}
                    />
                </>
            ) : (
                <div>
                    <LoadingBackdrop isOpen={true} />
                </div>
            )}
        </>
    )
}
